import { Pricing } from '../../types/cms.types';
import { supabase } from '../../config/supabase';

export interface PricingFilter {
    search?: string;
    province?: string;
    vehicle_type?: 'car' | 'motorbike';
    car_seats?: number;
}

export interface PricingListResponse {
    data: Pricing[];
    total: number;
    page: number;
    limit: number;
    totalPages: number;
}

export class PricingService {
    private static instance: PricingService;

    public static getInstance(): PricingService {
        if (!PricingService.instance) {
            PricingService.instance = new PricingService();
        }
        return PricingService.instance;
    }

    /**
     * Lấy danh sách pricing với phân trang và filter
     */
    async getPricings(
        page: number = 1,
        limit: number = 10,
        filters?: PricingFilter
    ): Promise<PricingListResponse> {
        try {
            let query = supabase
                .from('pricing')
                .select('*', { count: 'exact' });

            // Apply filters
            if (filters?.search) {
                query = query.ilike('province', `%${filters.search}%`);
            }

            if (filters?.province) {
                query = query.eq('province', filters.province);
            }

            if (filters?.vehicle_type) {
                query = query.eq('vehicle_type', filters.vehicle_type);
            }

            if (filters?.car_seats) {
                query = query.eq('car_seats', filters.car_seats);
            }

            // Apply pagination
            const offset = (page - 1) * limit;
            query = query.range(offset, offset + limit - 1);

            // Order by created_at desc
            query = query.order('created_at', { ascending: false });

            const { data, error, count } = await query;

            if (error) {
                console.error('Error fetching pricings:', error);
                throw error;
            }

            const totalPages = Math.ceil((count || 0) / limit);

            return {
                data: data || [],
                total: count || 0,
                page,
                limit,
                totalPages
            };
        } catch (error) {
            console.error('Error in getPricings:', error);
            throw error;
        }
    }

    /**
     * Lấy chi tiết pricing theo ID
     */
    async getPricingById(id: string): Promise<Pricing | null> {
        try {
            const { data, error } = await supabase
                .from('pricing')
                .select('*')
                .eq('id', id)
                .single();

            if (error) {
                console.error('Error fetching pricing:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error in getPricingById:', error);
            throw error;
        }
    }

    /**
     * Tạo pricing mới
     */
    async createPricing(pricing: Omit<Pricing, 'id' | 'created_at' | 'updated_at'>): Promise<Pricing> {
        try {
            const { data, error } = await supabase
                .from('pricing')
                .insert(pricing)
                .select()
                .single();

            if (error) {
                console.error('Error creating pricing:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error in createPricing:', error);
            throw error;
        }
    }

    /**
     * Cập nhật pricing
     */
    async updatePricing(id: string, pricing: Partial<Omit<Pricing, 'id' | 'created_at' | 'updated_at'>>): Promise<Pricing> {
        try {
            const { data, error } = await supabase
                .from('pricing')
                .update({
                    ...pricing,
                    updated_at: new Date().toISOString()
                })
                .eq('id', id)
                .select()
                .single();

            if (error) {
                console.error('Error updating pricing:', error);
                throw error;
            }

            return data;
        } catch (error) {
            console.error('Error in updatePricing:', error);
            throw error;
        }
    }

    /**
     * Xóa pricing
     */
    async deletePricing(id: string): Promise<void> {
        try {
            const { error } = await supabase
                .from('pricing')
                .delete()
                .eq('id', id);

            if (error) {
                console.error('Error deleting pricing:', error);
                throw error;
            }
        } catch (error) {
            console.error('Error in deletePricing:', error);
            throw error;
        }
    }

    /**
     * Lấy danh sách tỉnh thành từ pricing
     */
    async getProvinces(): Promise<string[]> {
        try {
            const { data, error } = await supabase
                .from('pricing')
                .select('province')
                .order('province');

            if (error) {
                console.error('Error fetching provinces:', error);
                throw error;
            }

            // Get unique provinces
            const uniqueProvinces: string[] = [];
            const provinceSet = new Set<string>();
            data?.forEach(item => {
                if (item.province && !provinceSet.has(item.province)) {
                    provinceSet.add(item.province);
                    uniqueProvinces.push(item.province);
                }
            });
            return uniqueProvinces;
        } catch (error) {
            console.error('Error in getProvinces:', error);
            throw error;
        }
    }

    /**
     * Sao chép bảng giá từ một tỉnh sang nhiều tỉnh khác
     * Nếu tỉnh đích đã có bảng giá thì sẽ cập nhật, chưa có thì tạo mới
     */
    async copyPricingToProvinces(
        sourcePricingId: string, 
        targetProvinces: string[]
    ): Promise<{ 
        success: number; 
        failed: number; 
        results: Array<{ province: string; success: boolean; error?: string }> 
    }> {
        try {
            // 1. Lấy thông tin bảng giá nguồn
            const sourcePricing = await this.getPricingById(sourcePricingId);
            if (!sourcePricing) {
                throw new Error('Không tìm thấy bảng giá nguồn');
            }

            // 2. Chuẩn bị dữ liệu để copy (loại bỏ các field không cần thiết)
            const {
                id,
                created_at,
                updated_at,
                province: sourceProvince,
                ...pricingDataToCopy
            } = sourcePricing;

            const results: Array<{ province: string; success: boolean; error?: string }> = [];
            let successCount = 0;
            let failedCount = 0;

            // 3. Thực hiện copy cho từng tỉnh thành
            for (const targetProvince of targetProvinces) {
                if (targetProvince === sourceProvince) {
                    results.push({
                        province: targetProvince,
                        success: false,
                        error: 'Không thể sao chép cho chính tỉnh nguồn'
                    });
                    failedCount++;
                    continue;
                }

                try {
                    // Kiểm tra xem tỉnh đích đã có bảng giá chưa
                    const { data: existingPricing } = await supabase
                        .from('pricing')
                        .select('id')
                        .eq('province', targetProvince)
                        .eq('vehicle_type', pricingDataToCopy.vehicle_type)
                        .eq('car_seats', pricingDataToCopy.car_seats)
                        .single();

                    if (existingPricing) {
                        // Cập nhật bảng giá hiện có
                        const { error: updateError } = await supabase
                            .from('pricing')
                            .update({
                                ...pricingDataToCopy,
                                province: targetProvince,
                                updated_at: new Date().toISOString()
                            })
                            .eq('id', existingPricing.id);

                        if (updateError) {
                            throw updateError;
                        }
                    } else {
                        // Tạo bảng giá mới
                        const { error: insertError } = await supabase
                            .from('pricing')
                            .insert({
                                ...pricingDataToCopy,
                                province: targetProvince
                            });

                        if (insertError) {
                            throw insertError;
                        }
                    }

                    results.push({
                        province: targetProvince,
                        success: true
                    });
                    successCount++;

                } catch (error) {
                    console.error(`Error copying pricing to ${targetProvince}:`, error);
                    results.push({
                        province: targetProvince,
                        success: false,
                        error: error instanceof Error ? error.message : 'Lỗi không xác định'
                    });
                    failedCount++;
                }
            }

            return {
                success: successCount,
                failed: failedCount,
                results
            };

        } catch (error) {
            console.error('Error in copyPricingToProvinces:', error);
            throw error;
        }
    }
}
